import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { ContainerTypeData, ContainerTypeCreate, ContainerTypeUpdate } from '../types';
import { containerTypeAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Search } = Input;

const ContainerTypes: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('container-types');
  const [containerTypes, setContainerTypes] = useState<ContainerTypeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<ContainerTypeData | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  useEffect(() => {
    loadContainerTypes();
  }, [showActiveOnly, searchText]);

  const loadContainerTypes = async () => {
    setLoading(true);
    try {
      const response = await containerTypeAPI.getContainerTypes({
        is_active: showActiveOnly ? true : undefined,
        search: searchText || undefined
      });
      setContainerTypes(response);
    } catch (error) {
      message.error('Errore nel caricamento dei tipi container');
      console.error('Error loading container types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingType(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setIsModalVisible(true);
  };

  const handleEdit = (record: ContainerTypeData) => {
    setEditingType(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await containerTypeAPI.deleteContainerType(id);
      message.success('Tipo container eliminato con successo');
      loadContainerTypes();
    } catch (error) {
      message.error('Errore nell\'eliminazione del tipo container');
      console.error('Error deleting container type:', error);
    }
  };

  const handleSubmit = async (values: ContainerTypeCreate | ContainerTypeUpdate) => {
    try {
      console.log('Submitting container type data:', values); // Debug
      if (editingType) {
        await containerTypeAPI.updateContainerType(editingType.id, values);
        message.success('Tipo container aggiornato con successo');
      } else {
        await containerTypeAPI.createContainerType(values);
        message.success('Tipo container creato con successo');
      }
      setIsModalVisible(false);
      form.resetFields();
      loadContainerTypes();
    } catch (error: any) {
      console.error('Error saving container type:', error);
      
      // Messaggio di errore più dettagliato
      let errorMessage = 'Errore nel salvataggio del tipo container';
      if (error.response?.data?.detail) {
        errorMessage = `Errore: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Errore: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `Errore: ${error.message}`;
      }
      
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: 'Codice',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (text: string, record: ContainerTypeData) => (
        <Space>
          <code>{text}</code>
          {!record.is_active && (
            <Tag color="red" size="small">Disattivato</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Descrizione',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Stato',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Attivo' : 'Disattivo'}
        </Tag>
      ),
    },
    {
      title: 'Creato',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 150,
      render: (_, record: ContainerTypeData) => (
        <Space size="small">
          <Tooltip title="Modifica">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Sei sicuro di voler eliminare questo tipo container?"
            description="Questa azione non può essere annullata."
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Tooltip title="Elimina">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="Gestione Tipi Container" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Cerca per codice o descrizione"
              allowClear
              onSearch={setSearchText}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <span>Solo attivi:</span>
              <Switch
                checked={showActiveOnly}
                onChange={setShowActiveOnly}
              />
            </Space>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space style={{ float: 'right' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadContainerTypes}
                title="Ricarica"
              />
              {canEdit && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  Nuovo Tipo Container
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={containerTypes}
          columns={canEdit ? columns : columns.filter((c) => c.key !== 'actions')}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} di ${total} tipi container`,
          }}
        />
      </Card>

      <Modal
        title={editingType ? 'Modifica Tipo Container' : 'Nuovo Tipo Container'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="code"
            label="Codice"
            rules={[
              { required: true, message: 'Inserire il codice del tipo container' },
              { max: 50, message: 'Il codice non può superare 50 caratteri' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Il codice può contenere solo lettere, numeri e underscore' }
            ]}
          >
            <Input placeholder="es: dry_van" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrizione"
            rules={[
              { required: true, message: 'Inserire la descrizione del tipo container' },
              { max: 200, message: 'La descrizione non può superare 200 caratteri' }
            ]}
          >
            <Input placeholder="es: Container Standard (Dry Van)" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Stato"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Attivo"
              unCheckedChildren="Disattivo"
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit">
                {editingType ? 'Aggiorna' : 'Crea'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContainerTypes;